"use client";

import React, { Context, createContext, useContext, useRef, useState } from "react";
import { useRouter } from "next/navigation";

import { UserProps, UserStatus, UserRole } from "@/types/user";
import { QrStatus } from "@/types/qr";
import { SubscriptionStatus } from "@/types/subscription";
import { UserContextProps } from "./types";
import { PlanProps } from "@/types/plan";
import { BillingData } from "@/containers/MyBillingContainer/components/BillingInformation/types";
import { getTokenData, removeAuthCookie } from "@/services/CookiesService";
import getProfileHandler from "@api/getProfile";
import { useApi } from "@/hooks/api/useApi";
import { userProfileApiToDataTransformer } from "@/services/UserService";
import { GetProfileResponse } from "@/interface/api/getProfile/types";
import { GetBillingPaymentsResponse } from "@/interface/api/getBillingPayments/types";
import { LayoutContext } from "@/contexts/LayoutContext";
import { paths } from "@/utils/paths";
import { getRoleFromToken } from "./utils";

const DEV_ROLE_STORAGE_KEY = "dev_role_override";
const isDev = process.env.NODE_ENV === "development";

export const USER_INITIAL_DATA: UserProps = {
  accountId: "",
  userId: undefined,
  firstName: "",
  lastName: "",
  phone: "",
  userStatus: UserStatus.REGISTERED,
  qrStatus: QrStatus.DRAFT,
  selectedSubscriptionId: "",
  email: "",
  freeTrialEndsAt: null,
  freeTrialStartsAt: null,
  status: UserStatus.REGISTERED,
  subscription: {
    id: "",
    status: SubscriptionStatus.REGISTERED,
    subscribed: false,
    plan: undefined,
  },
  locale: "en-US",
};

export const USER_BILLING_INITIAL_DATA: BillingData = {
  companyName: "",
  address: "",
  city: "",
  country: "",
  createdAt: "",
  firstName: "",
  id: "",
  lastName: "",
  phone: "",
  taxId: "",
  updatedAt: "",
  zipCode: "",
  legalStatus: "company",
};

export const USER_TRANSACTION_INITIAL_DATA: GetBillingPaymentsResponse = {
  items: [],
  total: 0,
};

let UserContext: Context<UserContextProps>;

const { Provider } = (UserContext = createContext<UserContextProps>(undefined));

const UserProvider = ({ children }: any) => {
  const layoutContext = useContext(LayoutContext);
  const [uiRole, setUiRole] = useState<UserRole | null>(null);
  const [prices, setPrices] = useState<PlanProps[]>([]);
  const [userData, setUserDataState] = useState<UserProps>(USER_INITIAL_DATA);
  const isFetchingRef = useRef(false);
  const fetchFailedRef = useRef(false);

  const router = useRouter();

  const { request: requestProfile } = useApi(getProfileHandler.getProfile);
  const [userBillingData, setUserBillingDataState] =
    useState<BillingData>(USER_BILLING_INITIAL_DATA);

  const [userTransactionData, setUserTransactionData] = useState<GetBillingPaymentsResponse>(
    USER_TRANSACTION_INITIAL_DATA
  );

  // Dev mode role override state
  const [devRoleOverride, setDevRoleOverrideState] = React.useState<UserRole | null>(null);

  // Initialize dev role from localStorage (only in development)
  React.useEffect(() => {
    if (isDev && typeof window !== "undefined") {
      const storedRole = localStorage.getItem(DEV_ROLE_STORAGE_KEY);
      if (storedRole && Object.values(UserRole).includes(storedRole as UserRole)) {
        setDevRoleOverrideState(storedRole as UserRole);
      }
    }
  }, []);

  // Listen for 401 unauthorized events to logout
  React.useEffect(() => {
    const handleForceLogout = () => {
      removeAuthCookie();
      isFetchingRef.current = false;
      fetchFailedRef.current = false;
      setUserDataState({ ...USER_INITIAL_DATA, userId: "" });
      router.push(paths.login());
    };

    window.addEventListener("force_logout", handleForceLogout);
    return () => {
      window.removeEventListener("force_logout", handleForceLogout);
    };
  }, [router]);

  const setDevRoleOverride = (role: UserRole | null) => {
    if (!isDev) return;

    setDevRoleOverrideState(role);
    if (typeof window !== "undefined") {
      if (role) {
        localStorage.setItem(DEV_ROLE_STORAGE_KEY, role);
      } else {
        localStorage.removeItem(DEV_ROLE_STORAGE_KEY);
      }
    }
  };

  // Compute effective role: dev override takes precedence in dev mode
  const effectiveRole = React.useMemo(() => {
    if (isDev && devRoleOverride) {
      return devRoleOverride;
    }
    return userData.role ?? UserRole.VIEWER;
  }, [devRoleOverride, userData.role]);

  const setUserData = (data: UserProps) => {
    setUserDataState(data);
  };

  // Workaround: useEffect doesn't run reliably after login navigation in Next.js App Router
  // This is because the UserProvider persists between client-side navigations.
  // We check if we need to fetch profile:
  // - userId is undefined (initial state) OR
  // - userId is empty string (previous failed attempt) AND there's now a valid token
  const tokenData = typeof window !== "undefined" ? getTokenData() : null;
  const hasValidToken = tokenData && !tokenData.isTokenExpired;
  const needsFetch =
    !isFetchingRef.current &&
    !fetchFailedRef.current &&
    (userData.userId === undefined || (userData.userId === "" && hasValidToken));

  if (typeof window !== "undefined" && needsFetch && layoutContext?.genericErrors) {
    isFetchingRef.current = true;

    const MAX_RETRIES = 5;
    const RETRY_DELAY = 500;

    const attemptFetchProfile = async (attempt: number) => {
      const currentToken = getTokenData();

      if (currentToken && !currentToken.isTokenExpired) {
        try {
          const response = await requestProfile({
            authorization: currentToken.token,
          });
          const profileData = response.data as GetProfileResponse;
          const formattedUser = userProfileApiToDataTransformer(profileData);
          const role = getRoleFromToken(currentToken.token);
          setUserDataState({ ...formattedUser, role });
        } catch (error: unknown) {
          console.error("Error fetching user profile:", error);

          const status = (error as any)?.response?.status;
          if (status === 401 || status === 403) {
            removeAuthCookie();
            router.push(paths.login());
            return;
          }

          fetchFailedRef.current = true;
          setUserDataState({
            ...USER_INITIAL_DATA,
            userId: "",
          });
        } finally {
          isFetchingRef.current = false;
        }
      } else if (attempt < MAX_RETRIES) {
        setTimeout(() => attemptFetchProfile(attempt + 1), RETRY_DELAY);
      } else {
        setUserDataState({
          ...USER_INITIAL_DATA,
          userId: "",
        });
        isFetchingRef.current = false;
      }
    };

    setTimeout(() => attemptFetchProfile(1), 100);
  }

  return (
    <Provider
      value={{
        user: userData,
        setUser: setUserData,
        uiRole,
        setUiRole,
        prices,
        setPrices,
        userBillingData: userBillingData,
        setUserBillingData: setUserBillingDataState,
        userTransactionData,
        setUserTransactionData,
        devRoleOverride,
        setDevRoleOverride,
        effectiveRole,
      }}
    >
      {children}
    </Provider>
  );
};

export { UserContext, UserProvider };
